import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { DialogDeleteConfirmationComponent } from '../dialogs/dialog-delete/dialog-delete-confirmation.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { User } from 'src/app/models/user';
import { GumUsersService } from 'src/app/services/gumservices/gum-users.service';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatIconModule,
    MatPaginator,
    MatTableModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
})
export class AdminComponent implements OnInit {
  displayedColumns = [
    'prenom',
    'nom',
    'email',
    'telephone',
    'fonction',
    'actions',
  ];
  dataSource = new MatTableDataSource<User>();
  filtersForm!: FormGroup;

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private filterMap: { [key: string]: string } = {
    prenom: 'Firstname',
    nom: 'Lastname',
    email: 'Email',
    telephone: 'Phone',
    fonction: 'Function',
  };

  constructor(
    private usersService: GumUsersService,
    private router: Router,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initFiltersForm();
    this.loadAdmins();
  }

  initFiltersForm(): void {
    this.filtersForm = this.fb.group({
      prenom: [''],
      nom: [''],
      email: [''],
      telephone: [''],
      fonction: [''],
    });
  }

  applyFilters(): void {
    const filterValues = this.filtersForm.value;

    this.dataSource.filterPredicate = (data: User, filter: string) => {
      const searchTerms = JSON.parse(filter);
      return Object.keys(searchTerms).every((key) => {
        const propName = this.filterMap[key];
        const value = this.getProperty(data, propName)?.toLowerCase() || '';
        return (
          !searchTerms[key] || value.includes(searchTerms[key].toLowerCase())
        );
      });
    };

    this.dataSource.filter = JSON.stringify(filterValues);
  }

  resetColumnFilter(column: string): void {
    this.filtersForm.get(column)?.setValue('');
    this.applyFilters();
  }

  loadAdmins(): void {
    this.usersService.getUsers().subscribe((users) => {
      const admins = users.filter((u) =>
        u.properties.some(
          (p) => p.name === 'NexusProfil' && p.value === 'NexusAdministrateur'
        )
      );
      this.dataSource = new MatTableDataSource(admins);
      this.dataSource.paginator = this.paginator;
      this.applyFilters();
    });
  }

  onCreate(): void {
    this.router.navigate(['/management/admin']);
  }

  onEdit(admin: User): void {
    this.router.navigate(['/management/admin'], {
      queryParams: { id: admin.id },
      state: { createdUserObject: admin },
    });
  }

  onDelete(admin: User): void {
    const dialogRef = this.dialog.open(DialogDeleteConfirmationComponent, {
      data: {},
    });

    const firstname = this.getProperty(admin, 'Firstname') || '';
    const lastname = this.getProperty(admin, 'Lastname') || '';

    dialogRef.componentInstance.confirmMessage = `Voulez-vous vraiment supprimer l’administrateur ${firstname} ${lastname} ?`;

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.usersService.deleteUser(admin.id).subscribe(() => {
          this.loadAdmins();
        });
      }
    });
  }

  getProperty(user: User, name: string): string {
    return user.properties.find((p) => p.name === name)?.value ?? '';
  }
}
